import React, { useCallback, useMemo, useState } from "react";
import Typography from "@material-ui/core/Typography";
import { Box, makeStyles } from "@material-ui/core";

import NewExpenseDialog from "./NewExpenseDialog";
import EditExpenseDialog from "./EditExpenseDialog";
import { asCurrency } from "../../../../../utils";
import {
  REPORT_ITEM_SUB_TYPE,
  REPORT_ITEM_TYPE
} from "../../../../../constants";
import isEqual from "../../../../../utils/isEqual";
import CustomCardHeader from "../../CustomCardHeader";
import Button from "components/CustomButtons/Button";
import Edit from "@material-ui/icons/Edit";

const useStyles = makeStyles({
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
});

const OperatingExpense = ({
  reportID,
  data,
  defaultData,
  chapterID,
  year,
  createReportItem,
  updateReportItem,
  updateReportItems,
  updateReportItemsPut,
  initialReportItems,
  removeReportItem,
  onRefetch,
  onHelp,
  onRedirect,
  isEditable
}) => {
  const classes = useStyles();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const items = useMemo(() => {
    return data.filter(
      item =>
        item.reportItemSubTypeID === REPORT_ITEM_SUB_TYPE.OPERATING_EXPENSE
    );
  }, [data]);

  const defaultItems = useMemo(() => {
    return defaultData.filter(
      item =>
        item.reportItemSubTypeID === REPORT_ITEM_SUB_TYPE.OPERATING_EXPENSE
    );
  }, [defaultData]);

  const presentedItems = items.length === 0 ? defaultItems : items;

  const isEqualToDefault = useMemo(
    () => isEqual(presentedItems, defaultItems),
    [presentedItems, defaultItems]
  );

  const onSaveEditDialog = useCallback(
    async ({ items, editedItems, removedItems }) => {
      if (reportID === "add") {
        const body = {
          chapterID,
          year,
          items: items.map(
            ({
              description,
              amount,
              reportItemSubTypeID,
              reportItemTypeID
            }) => ({
              description,
              amount: amount ? +amount : 0,
              reportItemTypeID,
              reportItemSubTypeID
            })
          )
        };
        const _reportID = await initialReportItems(body);
        onRedirect(_reportID);
      } else {
        if (isEqualToDefault) {
          const body = items.map(
            ({
              description,
              amount,
              reportItemSubTypeID,
              reportItemTypeID
            }) => ({
              description,
              amount: amount ? +amount : 0,
              reportID,
              reportItemTypeID,
              reportItemSubTypeID
            })
          );

          await updateReportItems(body);
        } else {
          var itemsToSend = [];
          itemsToSend = editedItems.map(
            ({
              id,
              description,
              amount,
              reportItemSubTypeID,
              reportItemTypeID
            }) => ({
              id,
              description,
              amount: +amount,
              staffPercent: 0,
              hoursPerWeek: 0,
              reportID,
              reportItemTypeID,
              reportItemSubTypeID
            })
          );

          await updateReportItemsPut(itemsToSend);

          await Promise.all(removedItems.map(({ id }) => removeReportItem(id)));
        }
      }
      onRefetch();
      setEditDialogOpen(false);
    },
    [reportID, chapterID, year]
  );

  const onSaveCreateDialog = useCallback(
    async ({ description, amount }) => {
      const addedItem = {
        description,
        amount: +amount,
        reportItemTypeID: REPORT_ITEM_TYPE.EXPENSE,
        reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.OPERATING_EXPENSE
      };
      const formItems = [
        ...defaultItems.map(
          ({ description, amount, reportItemSubTypeID, reportItemTypeID }) => ({
            description,
            amount: amount ? +amount : 0,
            reportItemTypeID,
            reportItemSubTypeID
          })
        ),
        addedItem
      ];

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID,
          year,
          items: formItems
        });
        onRedirect(_reportID);
      } else if (isEqualToDefault) {
        await updateReportItems(
          formItems.map(item => ({
            ...item,
            reportID
          }))
        );
      } else {
        await createReportItem({
          ...addedItem,
          reportID
        });
      }
      onRefetch();
      setCreateDialogOpen(false);
    },
    [reportID, chapterID, year]
  );

  return (
    <Box mb={3}>
      <CustomCardHeader
        label={"Operating Expenses"}
        onHelp={() =>
          onHelp(
            REPORT_ITEM_TYPE.EXPENSE,
            REPORT_ITEM_SUB_TYPE.OPERATING_EXPENSE
          )
        }
        extraContent={
          presentedItems.length !== 0 &&
          isEditable && (
            <Button
              simple
              color="primary"
              className={classes.actions}
              onClick={() => setEditDialogOpen(true)}
            >
              <Edit />
            </Button>
          )
        }
      />
      {presentedItems.map(item => (
        <Box
          display="flex"
          justifyContent="space-between"
          px={1}
          py={0.5}
          key={`revenue-${item.id}`}
        >
          <Typography>{item.description}</Typography>
          <Typography>{asCurrency(item.amount || 0)}</Typography>
        </Box>
      ))}
      {isEditable && (
        <Box ml={3} mt={2}>
          <a onClick={() => setCreateDialogOpen(true)}>
            <strong>Add another expense</strong>
          </a>
        </Box>
      )}
      <NewExpenseDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={onSaveCreateDialog}
      />
      <EditExpenseDialog
        open={editDialogOpen}
        data={presentedItems}
        onClose={() => setEditDialogOpen(false)}
        onSave={onSaveEditDialog}
      />
    </Box>
  );
};

export default OperatingExpense;
